// @owners { team: patients }
import {
  ActionSheetContext,
  Body,
  Card,
  Column,
  LgPadding,
  Pressable,
  Row,
  XlSpacing,
  XsSpacing,
  useScreenSize,
} from '@alto/design-system';
import { useNavigation } from '@alto/navigation';
import React, { useContext, useEffect } from 'react';
// eslint-disable-next-line import/no-deprecated
import { fetchFacilities } from '~shared/actions/facilities';
import rx from '~shared/assets/images/prescription@2x.png';
import { getCompanyName } from '~shared/config/getCompanyName';
import { useDispatchShared } from '~shared/store';
import { PharmacyInfoActionSheet } from './PharmacyInfoActionSheet';
import { Breadcrumbs } from '~web/components/Breadcrumbs';
import { ContentWrapper, Main, PageBackground } from '~web/components/Page';
import { PageHeader } from '~web/components/PageHeader';
import transfer from '~web/images/transfer@2x.png';

const BREADCRUMBS = [
  {
    text: 'Account',
    to: '/me',
  },
  {
    text: `Send prescriptions to ${getCompanyName()}`,
  },
];

export const SendPrescriptionOptions = () => {
  const { navigate, getParam } = useNavigation<'RouteTransferOptions'>();
  const origin = getParam('origin');
  const dispatch = useDispatchShared();
  const { setActiveActionSheet } = useContext(ActionSheetContext);

  useEffect(() => {
    // eslint-disable-next-line import/no-deprecated
    dispatch(fetchFacilities());
  }, [dispatch]);

  const { isSMScreenOrBigger } = useScreenSize();

  const handleViewPharmacyInfo = () => {
    setActiveActionSheet(<PharmacyInfoActionSheet />);
  };

  const handleStartTransfer = () => {
    navigate('RouteTransferPrescription', { origin });
  };

  return (
    <PageBackground>
      <ContentWrapper>
        <Column gap="MD">
          <Breadcrumbs breadcrumbs={BREADCRUMBS} />
          <PageHeader>Send Prescriptions</PageHeader>
        </Column>
        <Main>
          <Column>
            <>
              <Body fontFamily="bold">
                We make it easy and quick to bring your prescriptions to {getCompanyName()}.
              </Body>
              {isSMScreenOrBigger ? <XlSpacing /> : <XsSpacing />}
            </>
            <Row
              wrap
              gap="XL"
              stretchItems
            >
              <Pressable onPress={handleStartTransfer}>
                <Card
                  title="Transfer prescriptions"
                  illustrationSrc={transfer}
                  illustrationAccessibilityLabel="paper airplane representing doctor prescription"
                >
                  <LgPadding>
                    <Body>Let {getCompanyName()} get my prescriptions from another pharmacy.</Body>
                  </LgPadding>
                </Card>
              </Pressable>
              <Pressable onPress={handleViewPharmacyInfo}>
                <Card
                  title="View pharmacy info"
                  illustrationSrc={rx}
                  illustrationAccessibilityLabel="written prescription"
                >
                  <LgPadding>
                    <Body>Show {getCompanyName()}'s information to my doctor.</Body>
                  </LgPadding>
                </Card>
              </Pressable>
            </Row>
          </Column>
        </Main>
      </ContentWrapper>
    </PageBackground>
  );
};
